import * as Sentry from '@sentry/react';
import { useToast } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ClinicUserFilterType,
  SortOrder,
  TaskOrderByWithRelationAndSearchRelevanceInput,
  TaskStatus,
  TaskWhereInput,
  useCreateOneClinicUserFilterMutation,
  useFindUniqueClinicUserFilterQuery,
  useGetClinicUsersQuery,
  useUpdateOneClinicUserFilterMutation,
} from 'shared/types/graphql';
import { ASSIGNED_TO_ME, COMPLETED_BY_ME, taskGroupsMap, UNASSIGNED } from '../constants';
import {
  TaskGroup,
  TaskSortCriteria,
  TaskSortingKeys,
  TaskTabFilters,
  TypedFindUniqueClinicUserFilterQuery,
} from '../types';
import { buildTaskFilterFromClinicUser } from '../utils';

type UseTasksReturnType = {
  filters: TaskTabFilters;
  setFilters: React.Dispatch<React.SetStateAction<TaskTabFilters>>;
  clearFilters: () => void;
  hasFilters: boolean;
  quickSelectOptions: { id: string; name: string }[];
  teamMembersOptions: { id: string; name: string }[];
  statusOptions: { id: TaskStatus; name: string; color: string }[];
  saveFilters: () => Promise<void>;
  whereFilter: TaskWhereInput;
  activeTab: TaskGroup;
  setActiveTab: React.Dispatch<React.SetStateAction<TaskGroup>>;
  toggleTaskListCollapsibleState: (taskListId: string, isOpen: boolean) => void;
  expandedTaskListIds: string[];
  getSortCriteriaForList: (taskListId: string) => {
    queryOrderBy: TaskOrderByWithRelationAndSearchRelevanceInput;
    localOrderBy: TaskSortCriteria;
  };
  updateSorting: (key: TaskSortingKeys, taskListId: string) => void;
};

const defaultNeedsReviewOrderBy: TaskSortCriteria = {
  key: 'createdAt',
  sortOrder: SortOrder.Asc,
};

const defaultCompletedTasksOrderBy: TaskSortCriteria = {
  key: 'completedAt',
  sortOrder: SortOrder.Desc,
};

const emptyFilters: TaskTabFilters = {
  needsAttention: {
    status: [],
    assignees: [],
    expandedTaskListIds: [],
    sortingMap: {},
  },
  completed: {
    status: [],
    assignees: [],
    expandedTaskListIds: [],
    sortingMap: {},
  },
};

const needsAttentionQuickSelectOptions = [
  { id: UNASSIGNED, name: 'Unassigned' },
  { id: ASSIGNED_TO_ME, name: 'Assigned to Me' },
];

const completedQuickSelectOptions = [{ id: COMPLETED_BY_ME, name: 'Completed by Me' }];

const statusOptions = [
  {
    id: TaskStatus.Open,
    name: 'New',
    color: 'text.success',
  },
  {
    id: TaskStatus.InProgress,
    name: 'In Progress',
    color: 'text.warning',
  },
];

// Global state for task filters and where clause based on them, this will be merged with the individual conditions for each task list
// Hold clinic users to be used as assignees filter
export const useTasks = (): UseTasksReturnType => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<TaskGroup>(taskGroupsMap.needsAttention);
  const [filters, setFilters] = useState<TaskTabFilters>(emptyFilters);
  const { currentClinicId, clinicUserId } = useClinicUser();
  const { trackEvent } = useTracking();

  const { data: filtersData, refetch } = useFindUniqueClinicUserFilterQuery({
    variables: {
      where: {
        clinicId_userId_type: {
          clinicId: currentClinicId || '',
          userId: clinicUserId || '',
          type: ClinicUserFilterType.TaskManager,
        },
      },
    },
  });

  const existingFilter = filtersData?.findUniqueClinicUserFilter as TypedFindUniqueClinicUserFilterQuery;

  const { data: clinicUsers } = useGetClinicUsersQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const teamMembersOptions = useMemo(() => {
    if (!clinicUsers?.users?.length) return [];
    return clinicUsers.users.map((user) => {
      const { firstName, lastName, id } = user;
      return {
        id,
        name: `${firstName} ${lastName}`,
      };
    });
  }, [clinicUsers]);

  const [createUserFilter] = useCreateOneClinicUserFilterMutation();
  const [updateUserFilter] = useUpdateOneClinicUserFilterMutation();

  const hasFilters = useMemo(() => {
    return (
      existingFilter?.filter?.[activeTab.name]?.status?.length > 0 ||
      existingFilter?.filter?.[activeTab.name]?.assignees?.length > 0
    );
  }, [activeTab.name, existingFilter?.filter]);

  useEffect(() => {
    if (existingFilter) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        ...(existingFilter?.filter || {}),
      }));
    }
  }, [activeTab.name, existingFilter]);

  const clearFilters = useCallback(() => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [activeTab.name]: {
        ...emptyFilters[activeTab.name],
        expandedTaskListIds: currentFilters[activeTab.name]?.expandedTaskListIds || [],
        sortingMap: currentFilters[activeTab.name]?.sortingMap || {},
      },
    }));
  }, [activeTab.name]);

  const upsertFilters = useCallback(
    async (newFilters: TaskTabFilters) => {
      try {
        if (existingFilter) {
          if (isEqual(existingFilter.filter, newFilters)) {
            return;
          }
          await updateUserFilter({
            variables: {
              where: {
                clinicId_userId_type: {
                  clinicId: currentClinicId || '',
                  userId: clinicUserId || '',
                  type: ClinicUserFilterType.TaskManager,
                },
              },
              data: {
                filter: newFilters,
              },
            },
          });
        } else {
          if (isEqual(emptyFilters, newFilters)) {
            return;
          }
          await createUserFilter({
            variables: {
              data: {
                clinic: {
                  connect: {
                    id: currentClinicId || '',
                  },
                },
                user: {
                  connect: {
                    id: clinicUserId || '',
                  },
                },
                type: ClinicUserFilterType.TaskManager,
                filter: newFilters,
              },
            },
          });
        }

        refetch();
        trackEvent(TasksEvents.FilterSelectionApplied, {
          clinicId: currentClinicId,
        });
      } catch (e) {
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Oops, an error occurred while saving the filters.',
        });
      }
    },
    [clinicUserId, createUserFilter, currentClinicId, existingFilter, refetch, toast, trackEvent, updateUserFilter],
  );

  const saveFilters = useCallback(async (): Promise<void> => {
    upsertFilters(filters);
  }, [filters, upsertFilters]);

  const toggleTaskListCollapsibleState = useCallback(
    (taskListId: string, isOpen: boolean) => {
      setFilters((filters) => {
        const newFilters = {
          ...filters,
          [activeTab.name]: {
            ...filters[activeTab.name],
            expandedTaskListIds: isOpen
              ? [...(filters[activeTab.name].expandedTaskListIds || []), taskListId]
              : (filters[activeTab.name].expandedTaskListIds || []).filter((id) => id !== taskListId),
          },
        };
        upsertFilters(newFilters);
        return newFilters;
      });
    },
    [activeTab.name, upsertFilters],
  );

  const getQuerySortCriteria = useCallback(
    (currentSort: TaskSortCriteria): TaskOrderByWithRelationAndSearchRelevanceInput => {
      // Determine the sort order: either the provided sortOrder or a default based on the active tab.
      const sortOrder =
        currentSort.sortOrder ||
        (activeTab.name === 'needsAttention'
          ? defaultNeedsReviewOrderBy.sortOrder
          : defaultCompletedTasksOrderBy.sortOrder);

      // Split the key into parts and then reduce them from right to left to build a nested object.
      // For example, if currentSort.key is "clinicPetParent.firstName", the result will be:
      // { clinicPetParent: { firstName: sortOrder } }
      return currentSort.key
        .split('.')
        .reduceRight<TaskOrderByWithRelationAndSearchRelevanceInput>(
          (acc, key) => ({ [key]: acc }),
          sortOrder as TaskOrderByWithRelationAndSearchRelevanceInput,
        );
    },
    [activeTab.name],
  );

  const getSortCriteriaForList = useCallback(
    (taskListId: string) => {
      const sortingMap = filters[activeTab.name].sortingMap || {};
      return sortingMap[taskListId]
        ? {
            queryOrderBy: getQuerySortCriteria(sortingMap[taskListId]),
            localOrderBy: sortingMap[taskListId],
          }
        : {
            queryOrderBy: getQuerySortCriteria(
              'needsAttention' ? defaultNeedsReviewOrderBy : defaultCompletedTasksOrderBy,
            ),
            localOrderBy:
              activeTab.name === 'needsAttention' ? defaultNeedsReviewOrderBy : defaultCompletedTasksOrderBy,
          };
    },
    [filters, activeTab.name, getQuerySortCriteria],
  );

  const updateSorting = useCallback(
    (key: TaskSortingKeys, taskListId: string): void => {
      const { localOrderBy } = getSortCriteriaForList(taskListId);

      const sortOrder =
        localOrderBy?.key === key && localOrderBy.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;

      setFilters((filters) => {
        const newFilters = {
          ...filters,
          [activeTab.name]: {
            ...filters[activeTab.name],
            sortingMap: {
              ...filters[activeTab.name].sortingMap,
              [taskListId]: {
                key,
                sortOrder,
              },
            },
          },
        };
        upsertFilters(newFilters);
        return newFilters;
      });
    },
    [activeTab.name, getSortCriteriaForList, upsertFilters],
  );

  const whereFilter = useMemo(
    () =>
      buildTaskFilterFromClinicUser(
        existingFilter?.filter?.[activeTab.name],
        activeTab === taskGroupsMap.completed,
        clinicUserId,
      ),
    [activeTab, clinicUserId, existingFilter?.filter],
  );

  const filteredQuickSelectOptions = useMemo(
    () => (activeTab.name === 'needsAttention' ? needsAttentionQuickSelectOptions : completedQuickSelectOptions),
    [activeTab],
  );

  const expandedTaskListIds = useMemo(() => filters[activeTab.name].expandedTaskListIds || [], [filters, activeTab]);

  return {
    filters,
    setFilters,
    clearFilters,
    hasFilters,
    quickSelectOptions: filteredQuickSelectOptions,
    teamMembersOptions,
    statusOptions,
    saveFilters,
    whereFilter,
    activeTab,
    setActiveTab,
    toggleTaskListCollapsibleState,
    expandedTaskListIds,
    getSortCriteriaForList,
    updateSorting,
  };
};
